import { ShoppingCart } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { setCartTotalQuantity } from '~/redux/reducers/cart';
import { useCartsWithQuantityOverrides } from '~/hooks/useQuantityOverrides';
import type { RootState } from '~/redux/store';
import { useEffect } from 'react';
import {
  calculateOrderTotal,
  formatOrderTotal,
  formatUnitPrice,
  priceUnitLabel,
} from '~/utils';
import type { UnitCode } from '~/graphql/graphql';
import type { TallyStockSummary } from './OrderCalculator';

export type CartSummaryProps = {
  targetSize: number;
  platformProductUnit: UnitCode;
  actualWeeksCoverOverride?: number | null;
  targetWeeksOverride?: number | null;
  targetSizeOverride?: number | null;
  totalQuantityOverride?: number | null;
  totalValueOverride?: number | null;
  tallyStockSummary?: TallyStockSummary | null;
};

export function CartSummary({
  targetSize,
  platformProductUnit,
  actualWeeksCoverOverride,
  targetWeeksOverride,
  targetSizeOverride,
  totalQuantityOverride,
  totalValueOverride,
  tallyStockSummary,
}: Readonly<CartSummaryProps>) {
  const dispatch = useDispatch();
  const { t } = useTranslation(['order-builder', 'cart']);

  const serverCarts = useSelector((state: RootState) => state.cart.carts);
  const carts = useCartsWithQuantityOverrides(serverCarts);
  const pendingQuantitySaveCount = useSelector(
    (state: RootState) => state.cart.pendingQuantitySaveCount
  );
  const { inventory } = useSelector((state: RootState) => state.inventory);
  const isSavingQuantity = pendingQuantitySaveCount > 0;

  const usageRatePerWeek =
    inventory?.buyerInventoryConfiguration?.usageRatePerWeek?.value || 0;
  let totalQuantity = 0;

  carts.forEach((cart) => {
    cart.items?.forEach((item) => {
      totalQuantity += item.quantity.value;
    });
  });

  const currency = carts.find((cart) => cart.currency)?.currency ?? 'USD';

  // Sticky null: if any item across any cart is unpriced, the whole value is unknown.
  const totalValue = calculateOrderTotal({
    items: carts.flatMap((cart) => cart.items ?? []),
  });

  useEffect(() => {
    dispatch(setCartTotalQuantity(totalQuantity));
  }, [dispatch, totalQuantity]);

  const displayQuantity = totalQuantityOverride ?? totalQuantity;
  const displayValue = totalValueOverride ?? totalValue;

  const averagePrice =
    displayValue == null
      ? null
      : displayQuantity > 0
        ? displayValue / displayQuantity
        : 0;

  const weeksOfSupply = parseFloat(
    (displayQuantity / (usageRatePerWeek || 1)).toFixed(2)
  );

  const configuredTargetWeeks =
    inventory?.buyerInventoryConfiguration?.targetWeeksCovered ?? null;
  const stockValue = tallyStockSummary
    ? null
    : (inventory?.quantity?.value ?? null);
  const configuredTargetSize =
    configuredTargetWeeks != null
      ? Math.max(
          0,
          Math.floor(
            configuredTargetWeeks * usageRatePerWeek - (stockValue ?? 0)
          )
        )
      : null;
  const effectiveTargetSize =
    targetSizeOverride ?? configuredTargetSize ?? targetSize;
  const targetWeeksOfSupply = parseFloat(
    (effectiveTargetSize / (usageRatePerWeek || 1)).toFixed(2)
  );

  const effectiveActualWeeks = actualWeeksCoverOverride ?? weeksOfSupply;
  const effectiveTargetWeeks = targetWeeksOverride ?? targetWeeksOfSupply;

  return (
    <div className="px-6 py-4 space-y-4">
      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm pt-2">
          <span className="text-neut-sec">
            {t('order_builder.cart_summary.total_value')}
          </span>
          <span
            data-testid="summary-total-value"
            className="font-semibold text-neut-900"
          >
            {formatOrderTotal(
              totalValue,
              t('cart:cart.not_available'),
              currency
            )}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-neut-sec">
            {t('order_builder.cart_summary.avg_price')}
          </span>
          <span
            data-testid="summary-average-price"
            className="font-semibold text-success-600"
          >
            {averagePrice == null
              ? t('cart:cart.not_available')
              : `${formatUnitPrice(averagePrice, currency)}/${priceUnitLabel(platformProductUnit)}`}
          </span>
        </div>
      </div>
      {tallyStockSummary ? (
        <TallyStockCard
          summary={tallyStockSummary}
          unit={platformProductUnit}
        />
      ) : (
        <>
          <QuantityCard
            totalQuantity={displayQuantity}
            targetSize={effectiveTargetSize}
            unit={platformProductUnit}
            totalLabel={t('order_builder.cart_summary.total_quantity')}
            targetLabel={t('order_builder.cart_summary.target_size')}
          />
          <QuantityCard
            totalQuantity={effectiveActualWeeks}
            targetSize={effectiveTargetWeeks}
            unit={t('order_builder.order_calculator.units.weeks')}
            totalLabel={t('order_builder.cart_summary.actual_weeks_cover')}
            targetLabel={t('order_builder.cart_summary.target_weeks')}
          />
        </>
      )}
      <NavLink
        to="/cart"
        aria-disabled={isSavingQuantity}
        tabIndex={isSavingQuantity ? -1 : undefined}
        onClick={(e) => {
          if (isSavingQuantity) e.preventDefault();
        }}
        className={`w-full py-3 bg-neut-light text-neut-700 border border-neut-300 rounded-lg transition-colors flex items-center justify-center gap-2 font-medium ${
          isSavingQuantity
            ? 'opacity-50 pointer-events-none'
            : 'hover:bg-neut-50'
        }`}
      >
        <ShoppingCart className="w-4 h-4" aria-hidden="true" />
        {isSavingQuantity
          ? t('order_builder.cart_summary.saving_quantity')
          : t('order_builder.cart_summary.go_to_cart')}
      </NavLink>
    </div>
  );
}

type QuantityCardProps = {
  totalQuantity: number;
  currentQuantity?: number;
  targetSize: number;
  unit: string;
  targetLabel: string;
  totalLabel: string;
  currentLabel?: string;
};

function QuantityCard({
  totalQuantity,
  currentQuantity,
  targetSize,
  unit,
  targetLabel,
  totalLabel,
  currentLabel,
}: Readonly<QuantityCardProps>) {
  const { t } = useTranslation('order-builder');
  const effectiveTotal = totalQuantity + (currentQuantity ?? 0);
  const percentage =
    targetSize > 0 ? Math.min((effectiveTotal / targetSize) * 100, 100) : 100;
  const difference = Math.abs(effectiveTotal - targetSize).toFixed(2);

  return (
    <div className="py-2">
      <div className="flex items-center justify-between text-sm mb-2">
        <span className="text-neut-sec">{totalLabel}:</span>
        <span
          data-testid={`quantity-card-total-${totalLabel}`}
          className="font-bold text-neut-900"
        >
          {totalQuantity.toFixed(2)} {unit}
        </span>
      </div>
      {currentQuantity != null && currentLabel && (
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-neut-500">{currentLabel}:</span>
          <span className="text-neut-700">
            {currentQuantity.toFixed(2)} {unit}
          </span>
        </div>
      )}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-neut-500">{targetLabel}:</span>
          <span
            data-testid={`quantity-card-target-${targetLabel}`}
            className="text-neut-700"
          >
            {targetSize.toFixed(2)} {unit}
          </span>
        </div>
        <div className="relative h-1.5 bg-neut-100 rounded-full overflow-hidden">
          <div
            className={`absolute top-0 left-0 h-full transition-all duration-300 ${percentage < 100 ? 'bg-warning-600' : 'bg-success-600'} `}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
        <div
          className={`flex items-center justify-center text-xs ${percentage < 100 ? 'bg-warning-50' : 'bg-success-50'} px-2 py-1 rounded`}
        >
          <span
            data-testid={`quantity-card-difference-${totalLabel}`}
            className={`font-medium ${percentage < 100 ? 'text-warning-600' : 'text-success-600'}`}
          >
            {percentage < 100
              ? t('order_builder.cart_summary.short_by')
              : t('order_builder.cart_summary.excess')}
            : {difference} {unit}
          </span>
        </div>
      </div>
    </div>
  );
}

function lengthColor(weeksCovered: number | null, targetWeeks: number | null) {
  if (weeksCovered == null || targetWeeks == null || targetWeeks === 0) {
    return {
      bg: 'bg-success-50',
      label: 'text-success-600',
      value: 'text-success-700',
      bar: 'bg-success-300',
    };
  }
  if (weeksCovered >= targetWeeks) {
    return {
      bg: 'bg-success-50',
      label: 'text-success-600',
      value: 'text-success-700',
      bar: 'bg-success-300',
    };
  }
  if (weeksCovered >= targetWeeks * 0.5) {
    return {
      bg: 'bg-warning-50',
      label: 'text-warning-500',
      value: 'text-warning-700',
      bar: 'bg-warning-300',
    };
  }
  return {
    bg: 'bg-error-100',
    label: 'text-error-500',
    value: 'text-error-700',
    bar: 'bg-error-300',
  };
}

function TallyStockCard({
  summary,
  unit,
}: {
  summary: TallyStockSummary;
  unit: string;
}) {
  const { t } = useTranslation('order-builder');
  return (
    <div className="space-y-3" data-testid="tally-stock-preview">
      <div className="pb-2 border-b border-neut-100">
        <p className="text-xs font-semibold text-neut-700 mb-1.5">
          {t('order_builder.cart_summary.stock_preview')}
        </p>
        <p className="text-xs font-medium text-neut-700 mb-1.5">
          {t('order_builder.cart_summary.stock_by_length')}
        </p>
        <div className="flex flex-wrap gap-1 text-xs text-center">
          {summary.byLength.map(({ ft, stock, weeksCovered, targetWeeks }) => {
            const colors = lengthColor(weeksCovered, targetWeeks);
            return (
              <div
                key={ft}
                data-testid={`tally-stock-length-${ft}`}
                className={`${colors.bg} rounded px-1 py-1 min-w-12`}
              >
                <div className={`${colors.label} font-medium`}>{ft}ft</div>
                <div
                  data-testid={`tally-stock-value-${ft}`}
                  className={colors.value}
                >
                  {parseFloat(stock.toFixed(3))}
                </div>
                <div className={`h-0.5 mt-1 rounded-full ${colors.bar}`} />
                <div className="text-[10px] text-neut-400 mt-0.5">{unit}</div>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-neut-400 mt-1 text-right">
          {t('order_builder.cart_summary.total_stock_per_location')}
        </p>
      </div>
      {summary.usageRate != null && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-neut-600">
            {t('order_builder.cart_summary.usage_rate')}
          </p>
          <p className="text-sm font-semibold text-neut-900">
            {parseFloat(summary.usageRate.toFixed(3))} {unit}/
            {t('order_builder.order_calculator.units.weeks').toLowerCase()}
          </p>
        </div>
      )}
    </div>
  );
}
